<template>
    <m-pageframe :title="title">
        <v-container fluid v-if="params != null">
            <div class="outer_container">

                <div class="left_pane">
                    <v-btn color="primary" outlined class="mb-6" @click="onClickGoBack">
                        <v-icon left>mdi-arrow-left</v-icon>
                        Back to Lightboxes
                    </v-btn>

                    <!-- Configuration Section Group -->
                    <v-expansion-panels v-model="expanded_configuration" multiple class="section-group mb-6"
                        style="background-color: #fef7f7; border-radius: 8px; border-left: 4px solid #ffcdd2;">
                        <v-expansion-panel>
                            <v-expansion-panel-header style="background-color: transparent;">
                                <div class="overline text--secondary font-weight-bold">
                                    <v-icon small left color="error">mdi-cog-outline</v-icon>
                                    CONFIGURATION
                                </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content style="padding: 16px;">

                                <v-card elevation="1" class="mb-4">
                                    <v-card-text class="pa-4">
                                        <div class="subtitle-1 font-weight-medium mb-4">
                                            Basic Settings
                                        </div>

                                        <m-edit-text label="Lightbox Label" v-model="params.label" v-on:input="onChange"
                                            class="mb-3" />

                                        <m-edit-text label="Only Show Lightbox Once Per X Hours"
                                            v-model="hide_display_duration_hours" v-on:input="onHideDurationChange"
                                            hint="Number of hours to wait before showing the lightbox again after it has been dismissed (default: 24 hours)"
                                            type="number" :min="1" class="mb-3" />

                                        <m-edit-select-enum v-if="params" label="Visitor Authentication Visibility"
                                            v-model="params.visibility_for_authentication_status"
                                            enum_name="lightbox_visibility_for_authentication_status"
                                            :read_only="!has_enabled_authentication_systems" v-on:input="onChange" />

                                        <m-compatible-integrations-hint
                                            feature-identifier="use-site-visitor-authentication-status"
                                            before-link-text="Controls when the lightbox is shown based on visitor sign-in status. The website must be hosted by a"
                                            link-text="compatible system"
                                            after-link-text="for authentication detection to work."
                                            modal-title="Compatible Systems"
                                            modal-description="Authentication status detection is currently supported for websites hosted by the following systems:"
                                            no-integrations-message="No compatible systems are currently enabled for your organization."
                                            footer-message="More integrations will be added in the future. If your website is not hosted by a compatible system, the lightbox will default to 'Show regardless of authentication status' behavior."
                                        />
                                    </v-card-text>
                                </v-card>

                                <v-card elevation="1" class="mb-4">
                                    <v-card-text class="pa-4">
                                        <div class="subtitle-1 font-weight-medium mb-4">
                                            Container & Background
                                        </div>

                                        <div class="mb-4">


                                            <m-edit-css label="Background Blur" v-model="params.background_blur.style"
                                                :default_value="params.background_blur.default_style"
                                                v-on:input="onChange" class="mb-3" />

                                            <m-edit-css label="Lightbox Container" v-model="params.container.style"
                                                :default_value="params.container.default_style" v-on:input="onChange" />
                                        </div>
                                    </v-card-text>
                                </v-card>

                                <v-card elevation="1" class="mb-4">
                                    <v-card-text class="pa-4">
                                        <div class="subtitle-1 font-weight-medium mb-4">
                                            Header Content
                                        </div>

                                        <div class="mb-4">


                                            <v-checkbox
                                                label="Use Logo (when checked, logo will display instead of banner text)"
                                                class="mt-0 mb-3" v-model="params.use_logo" v-on:change="onChange"
                                                :color="params.use_logo ? 'primary' : 'grey'" />

                                            <m-edit-styled-text label="Banner Text" v-model="params.banner_text"
                                                v-on:input="onChange" v-show="!params.use_logo"
                                                hint="This text will show when use logo is unchecked or no logo is available"
                                                class="mb-3" />

                                            <m-edit-organization-logo v-model="params.logo_image.url"
                                                v-on:input="onChange" v-show="params.use_logo" class="mb-3" />

                                            <m-edit-css label="Logo Image Styling" v-model="params.logo_image.style"
                                                :default_value="params.logo_image.default_style" v-on:input="onChange"
                                                v-show="params.use_logo" class="mb-3" />
                                        </div>

                                        <m-edit-css label="Header Background" v-model="params.header_background.style"
                                            :default_value="params.header_background.default_style"
                                            v-on:input="onChange" class="mb-3" />

                                        <m-edit-css label="Close Icon" v-model="params.close_icon.style"
                                            :default_value="params.close_icon.default_style" v-on:input="onChange" />
                                    </v-card-text>
                                </v-card>

                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>

                    <!-- Design Section Group -->
                    <v-expansion-panels v-model="expanded_design" multiple class="section-group mb-6"
                        style="background-color: #fff5f5; border-radius: 8px; border-left: 4px solid #ffb3ba;">
                        <v-expansion-panel>
                            <v-expansion-panel-header style="background-color: transparent;">
                                <div class="overline text--secondary font-weight-bold">
                                    <v-icon small left color="error">mdi-form-select</v-icon>
                                    BEFORE SUBMISSION
                                </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content style="padding: 16px;">

                                <v-card elevation="1" class="mb-4">
                                    <v-card-text class="pa-4">
                                        <div class="subtitle-1 font-weight-medium mb-3">
                                            Form Content
                                        </div>
                                        <m-edit-styled-text label="Line 1" v-model="params.line_1"
                                            v-on:input="onChange" />

                                        <m-edit-styled-text label="Line 2" v-model="params.line_2"
                                            v-on:input="onChange" />

                                        <m-edit-styled-text label="Line 3" v-model="params.line_3"
                                            v-on:input="onChange" />
                                    </v-card-text>
                                </v-card>

                                <v-card elevation="2" class="mb-4">
                                    <v-card-text class="pa-4">
                                        <div class="subtitle-1 font-weight-medium mb-6">
                                            Form Fields
                                        </div>

                                        <!-- First Name -->
                                        <div class="mb-6 pa-3" style=" border-radius: 8px;">
                                            <v-select label="First Name" v-model="params.first_name.field_mode"
                                                :items="$rc.enumChoices('styled_field_mode', false)"
                                                v-on:input="onFieldModeChange('first_name', $event)" dense hide-details
                                                style="max-width: 300px;" class="mb-4" />

                                            <div v-show="params.first_name.field_mode !== 'hidden'" class="ml-4">
                                                <m-edit-styled-text label="First Name Label Text & Style"
                                                    v-model="first_name_label_text" v-on:input="onChange"
                                                    hint="Customize the label text and styling for the first name field"
                                                    class="mb-4" />

                                                <m-edit-css label="First Name Input Field Style"
                                                    v-model="params.first_name.style"
                                                    :default_value="params.first_name.default_style"
                                                    v-on:input="onChange"
                                                    hint="Style the input field background, border, font, and other CSS properties" />
                                            </div>
                                        </div>

                                        <!-- Last Name -->
                                        <div class="mb-6 pa-3" style=" border-radius: 8px;">
                                            <v-select label="Last Name" v-model="params.last_name.field_mode"
                                                :items="$rc.enumChoices('styled_field_mode', false)"
                                                v-on:input="onFieldModeChange('last_name', $event)" dense hide-details
                                                style="max-width: 300px;" class="mb-4" />

                                            <div v-show="params.last_name.field_mode !== 'hidden'" class="ml-4">
                                                <m-edit-styled-text label="Last Name Label Text & Style"
                                                    v-model="last_name_label_text" v-on:input="onChange"
                                                    hint="Customize the label text and styling for the last name field"
                                                    class="mb-4" />

                                                <m-edit-css label="Last Name Input Field Style"
                                                    v-model="params.last_name.style"
                                                    :default_value="params.last_name.default_style"
                                                    v-on:input="onChange"
                                                    hint="Style the input field background, border, font, and other CSS properties" />
                                            </div>
                                        </div>

                                        <!-- Birthday -->
                                        <div class="mb-6 pa-3" style="border-radius: 8px;">
                                            <v-select label="Birthday" v-model="params.birthday.field_mode"
                                                :items="getBirthdayFieldModeChoices()"
                                                v-on:input="onFieldModeChange('birthday', $event)" dense hide-details
                                                style="max-width: 300px;" class="mb-4" />

                                            <div v-show="params.birthday.field_mode !== 'hidden'" class="ml-4">


                                                <m-edit-styled-text label="Birthday Label Text & Style"
                                                    v-model="birthday_label_text" v-on:input="onChange"
                                                    hint="Customize the label text and styling for the birthday field"
                                                    class="mb-4" />

                                                <m-edit-css label="Birthday Input Field Style"
                                                    v-model="params.birthday.style"
                                                    :default_value="params.birthday.default_style" v-on:input="onChange"
                                                    hint="Style the input field background, border, font, and other CSS properties"
                                                    class="mb-4" />

                                                <m-edit-css label="Birthday Hint Style"
                                                    v-model="params.birthday.hint_style"
                                                    :default_value="params.birthday.default_hint_style"
                                                    v-on:input="onChange"
                                                    hint="Style the 'MM/DD/YYYY' hint and 'Must be 21+' error message text" />

                                                <m-edit-text label="Minimum Age (years)"
                                                    v-model="params.age_gate_min_age_in_years"
                                                    v-on:input="onAgeGateChange"
                                                    hint="Minimum age required for birthday validation (defaults to 21 if unset)"
                                                    class="mb-4" optional unset_label="Clear"
                                                    :rules="[ageGateValidationRule]" />
                                            </div>
                                        </div>

                                        <!-- Email Address -->
                                        <div class="mb-6 pa-3" style=" border-radius: 8px;">
                                            <v-select label="Email Address" v-model="params.email_address.field_mode"
                                                :items="$rc.enumChoices('styled_field_mode', false)"
                                                v-on:input="onFieldModeChange('email_address', $event)" dense
                                                hide-details style="max-width: 300px;" class="mb-4" />

                                            <div v-show="params.email_address.field_mode !== 'hidden'" class="ml-4">
                                                <m-edit-styled-text label="Email Address Label Text & Style"
                                                    v-model="email_address_label_text" v-on:input="onChange"
                                                    hint="Customize the label text and styling for the email field"
                                                    class="mb-4" />

                                                <m-edit-css label="Email Address Input Field Style"
                                                    v-model="params.email_address.style"
                                                    :default_value="params.email_address.default_style"
                                                    v-on:input="onChange"
                                                    hint="Style the input field background, border, font, and other CSS properties" />
                                            </div>
                                        </div>

                                        <!-- Phone Number -->
                                        <div class="mb-6 pa-3" style=" border-radius: 8px;">
                                            <v-select label="Phone Number" v-model="params.phone_number.field_mode"
                                                :items="$rc.enumChoices('styled_field_mode', false)"
                                                v-on:input="onFieldModeChange('phone_number', $event)" dense
                                                hide-details style="max-width: 300px;" class="mb-4" />

                                            <div v-show="params.phone_number.field_mode !== 'hidden'" class="ml-4">
                                                <m-edit-styled-text label="Phone Number Label Text & Style"
                                                    v-model="phone_number_label_text" v-on:input="onChange"
                                                    hint="Customize the label text and styling for the phone field"
                                                    class="mb-4" />

                                                <m-edit-css label="Phone Number Input Field Style"
                                                    v-model="params.phone_number.style"
                                                    :default_value="params.phone_number.default_style"
                                                    v-on:input="onChange"
                                                    hint="Style the input field background, border, font, and other CSS properties" />
                                            </div>
                                        </div>
                                    </v-card-text>
                                </v-card>

                                <v-card elevation="1" class="mb-4">
                                    <v-card-text class="pa-4">
                                        <div class="subtitle-1 font-weight-medium mb-4">
                                            Agreement Text
                                        </div>

                                        <m-edit-styled-text label="Agreement" v-model="params.agreement"
                                            v-on:input="onAgreementChange" />

                                        <v-alert v-if="show_agreement_warning" border="left" colored-border
                                            type="warning" elevation="1" class="mt-3" dense>
                                            <div class="caption">
                                                <strong>Legal Notice:</strong> Carefully consider changes made to this
                                                field. It may be a good idea to run changes by a lawyer for review.
                                            </div>
                                        </v-alert>
                                    </v-card-text>
                                </v-card>

                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>

                    <!-- After Submission Section Group -->
                    <v-expansion-panels v-model="expanded_actions" multiple class="section-group mb-6"
                        style="background-color: #fef9f9; border-radius: 8px; border-left: 4px solid #f8bbd9;">
                        <v-expansion-panel>
                            <v-expansion-panel-header style="background-color: transparent;">
                                <div class="overline text--secondary font-weight-bold">
                                    <v-icon small left color="error">mdi-heart-outline</v-icon>
                                    AFTER SUBMISSION
                                </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content style="padding: 16px;">

                                <v-card elevation="1" class="mb-4">
                                    <v-card-text class="pa-4">
                                        <div class="subtitle-1 font-weight-medium mb-4">
                                            Submit Button & Thank You Page
                                        </div>

                                        <div class="mb-4">
                                            <div class="subtitle-2 font-weight-medium mb-3">
                                                Submit Button
                                            </div>

                                            <m-edit-styled-text label="Send Button Text"
                                                v-model="params.send_button_text" v-on:input="onChange" class="mb-3" />

                                            <m-edit-css label="Send Button Style" v-model="params.send_button.style"
                                                :default_value="params.send_button.default_style"
                                                v-on:input="onChange" />
                                        </div>

                                        <div>
                                            <div class="subtitle-2 font-weight-medium mb-3">
                                                Thank You Page
                                            </div>

                                            <m-edit-styled-text label="Thank You Line 1"
                                                v-model="params.thank_you_line_1" v-on:input="onChange" class="mb-3" />

                                            <m-edit-styled-text label="Thank You Line 2"
                                                v-model="params.thank_you_line_2" v-on:input="onChange" class="mb-3" />

                                            <m-edit-styled-text label="Thank You Button Text"
                                                v-model="params.thank_you_button_text" v-on:input="onChange"
                                                class="mb-3" />

                                            <m-edit-css label="Thank You Button Style"
                                                v-model="params.thank_you_button.style"
                                                :default_value="params.thank_you_button.default_style"
                                                v-on:input="onChange" />
                                        </div>
                                    </v-card-text>
                                </v-card>

                                <v-card elevation="1" class="mb-4">
                                    <v-card-text class="pa-4">
                                        <div class="subtitle-1 font-weight-medium mb-3">
                                            Messaging Settings
                                        </div>
                                        <m-edit-select-organization-owned-endpoint
                                            :organization_id="$route.params.organization_id"
                                            v-model="params.endpoint_double_opt_in" v-on:input="onChange"
                                            label="Send double opt-in message from" include_bot_numbers />

                                        <m-edit-message-patterns-compact class="mb-6" label="Double opt-in message"
                                            v-model="params.double_opt_in_message_patterns" context="form-after-submit"
                                            v-on:input="onDoubleOptInChange"
                                            :default_value="getDefaultDoubleOptInMessagePatterns()" />

                                        <v-alert v-if="show_double_opt_in_warning" border="left" colored-border
                                            type="warning" elevation="1" class="mt-2 mb-3" dense>
                                            <div class="caption">
                                                <strong>Legal Notice:</strong> Carefully consider changes made to this
                                                field. It may be a good idea to run changes by a lawyer for review.
                                            </div>
                                        </v-alert>

                                        <m-edit-message-patterns-compact class="mb-6"
                                            label="After double opt-in message"
                                            v-model="params.after_double_opt_in_message_patterns"
                                            context="form-after-submit" v-on:input="onChange"
                                            :default_value="getDefaultAfterDoubleOptInMessagePatterns()" />
                                    </v-card-text>
                                </v-card>

                                <v-card elevation="1" class="mb-4">

                                    <v-card-text >
                                        <v-card flat color="grey lighten-5" class="mb-4 pa-3">
                                            <div class="d-flex align-center">
                                                <v-icon small class="mr-2">mdi-information-outline</v-icon>
                                                <div class="body-2">
                                                    <span v-if="$rc.getEnvironment() == 'dev'">
                                                        <strong>Development Environment:</strong> All submissions,
                                                        including previews, can sync to your integrations.
                                                    </span>
                                                    <span v-else>
                                                        <strong>Preview Protection:</strong> Syncing is always disabled for
                                                        preview submissions to avoid adding test data into your integrations.
                                                        Live submissions will continue to sync per your settings below.
                                                    </span>
                                                </div>
                                            </div>
                                        </v-card>

                                        <div class="subtitle-1 font-weight-medium mb-3">
                                            CRM Integration Settings
                                        </div>

                                        <m-compatible-integrations-hint
                                            feature-identifier="customers-auto-enrich-from-lightbox-submission"
                                            before-link-text="CRM integration features require a"
                                            link-text="compatible CRM integration"
                                            after-link-text="to be enabled for your organization."
                                            modal-title="Compatible CRM Integrations"
                                            modal-description="Customer enrichment from lightbox submissions is currently supported for the following CRM integrations:"
                                            no-integrations-message="No compatible CRM integrations are currently enabled for your organization."
                                            footer-message="More integrations will be added in the future. If your CRM is not listed above, the lightbox will still function but customer data will not be automatically synced to your CRM."
                                            :show-hint="!customer_auto_enrich_feature_available"
                                        />

                                        <m-edit-select-enum label="On Form Submission"
                                            enum_name="crm_customer_upsert_mode"
                                            v-model="params.on_submit_crm_customer_upsert_mode" v-on:input="onChange"
                                            :read_only="!customer_auto_enrich_feature_available"
                                            :option_descriptions="crm_upsert_mode_descriptions"
                                            hint="Action to take in your CRM when a customer submits the form." />

                                        <m-edit-select-enum label="After Double Opt-In"
                                            enum_name="crm_customer_upsert_mode"
                                            v-model="params.after_double_opt_in_crm_customer_upsert_mode"
                                            v-on:input="onChange" :read_only="!customer_auto_enrich_feature_available"
                                            :option_descriptions="crm_upsert_mode_descriptions"
                                            hint="Action to take in your CRM after a customer confirms their subscription." />

                                        <!-- New Customer Tag Selection -->
                                        <v-select v-if="showTagSelection" label="Tag for New Customers"
                                            v-model="params.new_customer_tag_id" :items="integration_tags_with_none"
                                            item-text="tag_label" item-value="tag_id" clearable
                                            :disabled="!customer_auto_enrich_feature_available"
                                            hint="Select a tag to automatically apply to newly created customers from lightbox submissions. This tag will NOT be applied to existing customers. Select 'No Tag' to disable tagging."
                                            persistent-hint class="mt-3" @change="onNewCustomerTagChange">
                                            <template v-slot:no-data>
                                                <v-list-item>
                                                    <v-list-item-content>
                                                        <v-list-item-title>No tags available</v-list-item-title>
                                                    </v-list-item-content>
                                                </v-list-item>
                                            </template>
                                        </v-select>

                                        <div class="subtitle-1 font-weight-medium mb-3 mt-6">
                                            RedChirp Integration Settings
                                        </div>

                                        <!-- List Selection for After Double Opt-In -->
                                        <v-select label="After double opt-in, add contact to list(s)"
                                            v-model="params.add_to_lists" :items="available_lists" item-text="text"
                                            item-value="value" v-on:change="onChange" multiple chips clearable
                                            persistent-hint
                                            hint="At minimum, you must have a phone number in the form submission to add the customer to a list.">
                                            <template v-slot:no-data>
                                                <v-list-item>
                                                    <v-list-item-content>
                                                        <v-list-item-title>No lists available</v-list-item-title>
                                                    </v-list-item-content>
                                                </v-list-item>
                                            </template>
                                        </v-select>
                                    </v-card-text>
                                </v-card>

                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>



                    <!-- Preview Section Group -->
                    <v-expansion-panels v-model="expanded_preview" multiple class="section-group mb-6"
                        style="background-color: #fff0f0; border-radius: 8px; border-left: 4px solid #ffa1a1;">
                        <v-expansion-panel>
                            <v-expansion-panel-header style="background-color: transparent;">
                                <div class="overline text--secondary font-weight-bold">
                                    <v-icon small left color="error">mdi-eye-outline</v-icon>
                                    PREVIEW & TESTING
                                </div>
                            </v-expansion-panel-header>
                            <v-expansion-panel-content style="padding: 16px;">

                                <v-card elevation="2" class="mb-4">
                                    <v-card-text class="pa-4">
                                        <div class="subtitle-1 font-weight-medium mb-3 d-flex align-center">
                                            Preview Controls
                                            <v-chip small color="error" text-color="white" class="ml-2">Live
                                                Preview</v-chip>

                                            <!-- Show Controls Button -->
                                            <v-btn v-if="!floating_control_visible" x-small color="error" text
                                                @click="onToggleFloatingControl" class="control-restore-btn ml-3">
                                                <v-icon x-small left>mdi-eye-outline</v-icon>
                                                Show Minimized Controls
                                            </v-btn>
                                        </div>
                                        <v-row>
                                            <v-col cols="12" md="6">
                                                <v-subheader class="px-0">
                                                    Lightbox Actions
                                                </v-subheader>
                                                <v-btn-toggle :value="lightbox_visible ? 0 : 1" mandatory class="d-flex"
                                                    color="primary">
                                                    <v-btn v-on:click="onClickShowLightbox" class="flex-grow-1"
                                                        :class="{ 'v-btn--active': lightbox_visible }">
                                                        <v-icon left>mdi-eye</v-icon>
                                                        Show
                                                    </v-btn>
                                                    <v-btn v-on:click="onClickHideLightbox" class="flex-grow-1"
                                                        :class="{ 'v-btn--active': !lightbox_visible }">
                                                        <v-icon left>mdi-eye-off</v-icon>
                                                        Hide
                                                    </v-btn>
                                                </v-btn-toggle>
                                            </v-col>

                                            <v-col cols="12" md="6">
                                                <v-subheader class="px-0">
                                                    Preview Mode
                                                </v-subheader>
                                                <v-btn-toggle v-model="preview_mode" mandatory color="primary"
                                                    class="d-flex">
                                                    <v-btn value="desktop" class="flex-grow-1">
                                                        Desktop
                                                    </v-btn>
                                                    <v-btn value="mobile" class="flex-grow-1">
                                                        Mobile
                                                    </v-btn>
                                                </v-btn-toggle>
                                            </v-col>
                                        </v-row>

                                        <v-row class="mt-2">
                                            <v-col cols="12">
                                                <v-subheader class="px-0">
                                                    Page Preview
                                                </v-subheader>
                                                <v-btn-toggle :value="current_page === 'form' ? 0 : 1" mandatory
                                                    class="d-flex" color="primary">
                                                    <v-btn v-on:click="onClickShowLightbox" class="flex-grow-1"
                                                        :class="{ 'v-btn--active': current_page === 'form' }">
                                                        <v-icon left>mdi-form-select</v-icon>
                                                        Form Page
                                                    </v-btn>
                                                    <v-btn v-on:click="onClickShowThankYou" class="flex-grow-1"
                                                        :class="{ 'v-btn--active': current_page === 'thanks' }">
                                                        <v-icon left>mdi-heart</v-icon>
                                                        Thank You Page
                                                    </v-btn>
                                                </v-btn-toggle>
                                            </v-col>
                                        </v-row>
                                    </v-card-text>
                                </v-card>

                            </v-expansion-panel-content>
                        </v-expansion-panel>
                    </v-expansion-panels>






                    <!--<v-divider
                style="margin-top: 15px; margin-bottom: 15px"
           />

            <div
                style="margin-top: 15px"
            >
                <pre>{{ JSON.stringify(params, null, 2) }}</pre>
            </div>-->

                </div>

                <div class="right_pane_container" :class="{ 'mobile-preview': preview_mode === 'mobile' }">
                    <!-- Compact Control Bar - Top Left of Preview -->
                    <m-lightbox-floating-preview-control :visible="floating_control_visible"
                        :preview_mode="preview_mode" :current_page="current_page" :lightbox_visible="lightbox_visible"
                        @show-lightbox="onClickShowLightbox" @hide-lightbox="onClickHideLightbox"
                        @show-thank-you="onClickShowThankYou" @preview-mode-change="onFloatingControlPreviewModeChange"
                        @dismiss="onDismissFloatingControl" />



                    <div class="iframe-wrapper" :class="{ 'mobile-frame': preview_mode === 'mobile' }">
                        <iframe class="right_pane_iframe" :src="iframe_src_url" ref="lightbox-iframe" />
                    </div>
                </div>



            </div>



        </v-container>
    </m-pageframe>
</template>

<style scoped>
.logo-disabled {
    opacity: 0.6;
    pointer-events: none;
}

.logo-disabled>>>.v-input {
    opacity: 0.6;
}

.logo-disabled>>>.v-text-field {
    opacity: 0.6;
}

.section-group {
    transition: all 0.3s ease;
}

.v-card {
    transition: all 0.2s ease;
}

.overline {
    letter-spacing: 1.5px;
    font-size: 0.75rem !important;
}
</style>

<script>
import mEditCss from '../../components/input/m-edit-css.vue';
import mLightboxFloatingPreviewControl from '../../components/lightbox/m-lightbox-floating-preview-control.vue';

// LOGO URL: Pur Noire: https://files.redchirp.com/yrvatevqbrbeuxaaaayfdqzqbbqztfww.png
// LOGO URL: Barrel Creek: https://files.redchirp.com/cvctraabvftrufcaytwyyqyruxuzryzt.png

const _ = require('lodash');

export default {
    components: {
        mEditCss,
        mLightboxFloatingPreviewControl
    },

    data() {
        return {
            params: null,
            organization_lightboxes: null,
            first_load: true,
            customer_auto_enrich_feature_available: false,
            commerce7_integration_available: false,
            tag_new_lightbox_customers_available: false,
            integration_tags: [], // Generic tags from the current integration (Commerce7 for now)
            available_lists: [], // Available lists for selection
            preview_mode: 'desktop', // 'desktop' or 'mobile'
            show_agreement_warning: false,
            show_double_opt_in_warning: false,
            original_agreement: null,
            original_double_opt_in: null,
            expanded_configuration: [0], // Configuration section expanded by default
            expanded_design: [0], // Design section expanded by default
            expanded_actions: [0], // Actions section expanded by default
            expanded_preview: [0], // Preview section expanded by default
            floating_control_visible: true, // Preview control bar visibility
            current_page: 'form', // Track current page: 'form' or 'thanks'
            lightbox_visible: true, // Track if lightbox is currently visible
            has_enabled_authentication_systems: false,
            ageGateValidationRule: (value) => {
                // Allow null/empty (optional field)
                if (value == null || value === '') return true;

                // If value provided, must be positive non-zero integer
                let int_value = Number(value);
                if (isNaN(int_value) || int_value < 1 || int_value != parseInt(value)) {
                    return 'Must be a positive integer greater than 0';
                }
                return true;
            }
        }
    },

    mounted() {
        this.loadSettings();
        this.loadOrganizationLightboxes();
        this.loadIntegrationFeatures();
        this.loadAvailableLists();
        this.$rc.loadCustomFonts(this.$route.params.organization_id);
        window.addEventListener('message', this.onMessage, false);
    },

    unmounted() {
        window.removeEventListener('message', this.onMessage);
    },

    methods: {
        onMessage(event) {
            if (event == null || event.data == null || !Array.isArray(event.data) || event.data.length <= 0)
                return;

            let message_type = event.data[0];

            if (message_type == 'redchirp-lightbox-ready') {
                if (this.params != null) {
                    this.sendMessage('redchirp-lightbox-show');
                }
            }

            if (message_type == 'redchirp-lightbox-form-submitted') {
                // Form was submitted, lightbox is now showing thank you page
                this.current_page = 'thanks';
                this.lightbox_visible = true;
            }

            if (message_type == 'redchirp-lightbox-close') {
                // Lightbox was closed (X button or OK on thank you page)
                this.lightbox_visible = false;
                // Keep current page state when closing
            }
        },

        loadSettings() {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'GET: /api/v1/lightbox/settings/:organization_id/:lightbox_id',

                'params': {
                    'organization_id': this.$route.params.organization_id,
                    'lightbox_id': this.$route.params.lightbox_id,
                },

                'on_success': (data) => {
                    this.params = _.cloneDeep(data);

                    // Initialize add_to_lists if it doesn't exist
                    if (!this.params.add_to_lists) {
                        this.params.add_to_lists = [];
                    }

                    // Store original values for comparison
                    if (this.original_agreement === null) {
                        this.original_agreement = _.cloneDeep(this.params.agreement);
                    }
                    if (this.original_double_opt_in === null) {
                        this.original_double_opt_in = _.cloneDeep(this.params.double_opt_in_message_patterns);
                    }

                    if (this.first_load) {
                        this.first_load = false;

                        window.setTimeout(() => this.sendMessage('redchirp-lightbox-show'), 1000);
                    }
                    else {
                        this.sendMessage('redchirp-lightbox-set-settings');
                    }
                },

                'on_error': 'Unable to load lightbox settings',
            });
        },

        sendMessage(message_type) {
            if (this.params == null)
                return;

            let iframe = this.$refs['lightbox-iframe'];

            if (iframe == null)
                return;

            this.params.is_test_submission = true;

            let message = [message_type];

            if (message_type == 'redchirp-lightbox-set-settings' || message_type == 'redchirp-lightbox-show' || message_type == 'redchirp-lightbox-show-thanks')
                message.push(this.params);

            iframe.contentWindow.postMessage(
                message,
                '*'
            );
        },

        // Helper methods for label text computed properties
        getLabelTextData(field_name) {
            if (!this.params || !this.params[field_name]) {
                return { text: '', style: '', default_text: '', default_style: '' };
            }

            return {
                text: this.params[field_name].text || '',
                style: this.params[field_name].label_style || '',
                default_text: this.params[field_name].default_text || '',
                default_style: this.params[field_name].default_label_style || ''
            };
        },

        setLabelTextData(field_name, value) {
            if (this.params && this.params[field_name]) {
                this.params[field_name].text = value.text;
                this.params[field_name].label_style = value.style;
            }
        },


        loadOrganizationLightboxes() {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'GET:/api/v1/lightbox/organization/:organization_id',

                'params': {
                    'organization_id': this.$route.params.organization_id,
                },

                'on_success': (data) => {
                    this.organization_lightboxes = _.cloneDeep(data);
                },

                'on_error': 'Unable to load organization lightbox data',
            });
        },

        loadAvailableLists() {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'LIST: /api/v1/autocomplete/lists',

                'params': {},

                'on_success': (data) => {
                    let items = [];

                    for (let i = 0; i < data.results.length; i++) {
                        items.push({
                            'text': data.results[i].text,
                            'value': data.results[i].value,
                        });
                    }

                    this.available_lists = items;
                },

                'on_error': 'Unable to load available lists',
            });
        },

        onClickShowLightbox() {
            this.sendMessage('redchirp-lightbox-show');
            this.current_page = 'form';
            this.lightbox_visible = true;
        },

        onClickHideLightbox() {
            this.sendMessage('redchirp-lightbox-hide');
            this.lightbox_visible = false;
            // Keep current page state when hiding
        },

        onClickShowThankYou() {
            this.sendMessage('redchirp-lightbox-show-thanks');
            this.current_page = 'thanks';
            this.lightbox_visible = true;
        },

        onToggleFloatingControl() {
            this.floating_control_visible = !this.floating_control_visible;
        },

        onDismissFloatingControl() {
            this.floating_control_visible = false;
        },



        onFloatingControlPreviewModeChange(mode) {
            this.preview_mode = mode;
            // Reset to form page when preview mode changes via floating controls
            this.current_page = 'form';
            this.lightbox_visible = true;
            this.onClickShowLightbox();
        },

        onChange: _.debounce(function () {
            this.doSaveSettings();
        }, 750),

        onFieldModeChange(fieldName, newValue) {
            // Update the field mode
            this.params[fieldName].field_mode = newValue;

            // Force preview refresh by hiding, updating settings, then showing
            // This replicates the manual hide/show that works
            this.sendMessage('redchirp-lightbox-hide');
            this.sendMessage('redchirp-lightbox-set-settings');
            this.sendMessage('redchirp-lightbox-show');

            // Save changes to the server
            this.onChange();
        },

        onAgreementChange() {
            // Check if agreement has been modified from original
            if (this.original_agreement && !_.isEqual(this.params.agreement.text, this.original_agreement.text)) {
                this.show_agreement_warning = true;
            } else {
                this.show_agreement_warning = false;
            }
            this.onChange();
        },

        onDoubleOptInChange() {
            // Check if double opt-in has been modified from original
            if (this.original_double_opt_in && !_.isEqual(this.params.double_opt_in_message_patterns, this.original_double_opt_in)) {
                this.show_double_opt_in_warning = true;
            } else {
                this.show_double_opt_in_warning = false;
            }
            this.onChange();
        },

        onHideDurationChange() {
            this.onChange();
        },

        onAgeGateChange(value) {
            // Convert string input to integer, or null if empty
            if (value === '' || value == null) {
                this.params.age_gate_min_age_in_years = null;
            }
            else {
                const intValue = parseInt(value, 10);
                if (!isNaN(intValue) && intValue > 0) {
                    this.params.age_gate_min_age_in_years = intValue;
                }
            }
            this.onChange();
        },

        doSaveSettings() {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/lightbox/settings',

                'params': this.params,

                'on_success': (data) => {
                    this.params = data;
                    this.sendMessage('redchirp-lightbox-set-settings');
                },

                'on_error': 'Unable to save lightbox settings',
            });
        },



        onClickGoBack() {
            this.$router.push({
                'name': 'lightbox-search-settings',
                'params': {
                    'organization_id': this.$route.params.organization_id,
                }
            });
        },

        /**
         * Load all integration features needed for lightbox functionality.
         * This consolidates multiple feature checks into a single maintainable method.
         */
        loadIntegrationFeatures() {
            // Check customer auto-enrichment feature
            this.checkFeatureAvailability(
                'customers-auto-enrich-from-lightbox-submission',
                (is_supported) => {
                    this.customer_auto_enrich_feature_available = is_supported;
                }
            );

            // Check customer tagging feature and load tags if available
            this.checkFeatureAvailability(
                'tag-new-lightbox-customers-from-lightbox-submission',
                (is_supported) => {
                    // Only load tags if the tagging feature is available
                    this.tag_new_lightbox_customers_available = is_supported;
                    if (is_supported) {
                        // Currently we don't expose the integration type to the user, so we hardcode Commerce7 for now
                        this.loadIntegrationTags('COMMERCE7');
                    }
                    else {
                        this.integration_tags = [];
                    }
                }
            );



            // Check if user has enabled integrations that support authentication status
            this.checkFeatureAvailability(
                'use-site-visitor-authentication-status',
                (is_supported) => {
                    this.has_enabled_authentication_systems = is_supported;
                }
            );
        },

        /**
         * Generic helper method to check if a specific integration feature is available.
         *
         * @param {string} feature_identifier - The feature to check
         * @param {function} on_success - Callback with boolean result
         */
        checkFeatureAvailability(feature_identifier, on_success) {
            this.$rc.hasEnabledIntegrationFeature(
                feature_identifier,
                on_success,
                () => {
                    // On error, assume feature is not available
                    on_success(false);
                }
            );
        },

        /**
         * Load available integration tags for tagging new customers.
         * Currently loads Commerce7 tags, but could be extended for other integrations.
         */
        loadIntegrationTags(integration_type) {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'GET: /api/v1/integrations/crm/tags/' + this.$route.params.organization_id + '/' + integration_type,

                'on_success': (data) => {
                    this.integration_tags = data.tags || [];
                },

                'on_error': () => {
                    this.integration_tags = [];
                }
            });
        },

        getDefaultDoubleOptInMessagePatterns() {
            if (this.organization_lightboxes && this.organization_lightboxes.default_double_opt_in_message)
                return [this.organization_lightboxes.default_double_opt_in_message];

            return [];
        },

        getDefaultAfterDoubleOptInMessagePatterns() {
            if (this.organization_lightboxes && this.organization_lightboxes.default_after_double_opt_in_message)
                return [this.organization_lightboxes.default_after_double_opt_in_message];

            return [];
        },

        getBirthdayFieldModeChoices() {
            let choices = this.$rc.enumChoices('styled_field_mode', false);
            // For wine organizations, birthday field cannot be hidden or optional
            if (this.organization_lightboxes && this.organization_lightboxes.comp_organization_industry === 'wine') {
                choices = choices.filter(choice => choice.value !== 'hidden' && choice.value !== 'optional');
                this.params.birthday.field_mode = 'required';
            }

            return choices;
        },

        onNewCustomerTagChange(tag_id) {
            // Automatically set the integration type to COMMERCE7 when a tag is selected (other than null/NONE)
            // If we enable tagging for other integrations in the future we should allow users to select the integration type
            if (tag_id && tag_id !== null) {
                this.params.new_customer_tag_integration_type = 'COMMERCE7';
            } else {
                // Clear the integration type when no tag is selected
                this.params.new_customer_tag_integration_type = null;
            }

            // Trigger the onChange to save the settings
            this.onChange();
        },

    },

    computed: {
        iframe_src_url() {
            let base_url;
            if (this.$rc.getEnvironment() == 'dev')
                base_url = 'http://localhost:5173/index.html';
            else if (this.$rc.getEnvironment() == 'staging')
                base_url = 'https://files.redchirp.com/v1/prod/widget/staging-lightbox.html';
            else
                base_url = 'https://files.redchirp.com/v1/prod/widget/lightbox.html';

            // Add mobile parameter when in mobile preview mode
            if (this.preview_mode === 'mobile') {
                const separator = base_url.includes('?') ? '&' : '?';
                return base_url + separator + 'mobile=true';
            }

            return base_url;
        },

        title() {
            if (this.params == null)
                return 'Edit Lightbox';

            return this.params.label;
        },


        crm_upsert_mode_descriptions() {
            return {
                'do-nothing': 'No customer information will be sent to your CRM system. Use this if you want to collect contact information but handle CRM updates manually.',
                'create-only': 'Only create new customer records in your CRM when someone submits the form. If the customer already exists in your CRM, their information will not be updated.',
                'update-only': 'Only update existing customer records in your CRM. If someone submits the form but is not already a customer in your CRM, no new record will be created. This is useful for updating information for known customers only.',
                'create-or-update': 'Automatically create new customer records for new contacts and update existing records for known customers. This provides full synchronization between form submissions and your CRM system.'
            };
        },

        // Computed properties for label text editing
        first_name_label_text: {
            get() {
                return this.getLabelTextData('first_name');
            },
            set(value) {
                this.setLabelTextData('first_name', value);
            }
        },

        last_name_label_text: {
            get() {
                return this.getLabelTextData('last_name');
            },
            set(value) {
                this.setLabelTextData('last_name', value);
            }
        },

        birthday_label_text: {
            get() {
                return this.getLabelTextData('birthday');
            },
            set(value) {
                this.setLabelTextData('birthday', value);
            }
        },

        email_address_label_text: {
            get() {
                return this.getLabelTextData('email_address');
            },
            set(value) {
                this.setLabelTextData('email_address', value);
            }
        },

        phone_number_label_text: {
            get() {
                return this.getLabelTextData('phone_number');
            },
            set(value) {
                this.setLabelTextData('phone_number', value);
            }
        },

        hide_display_duration_hours: {
            get() {
                if (!this.params || this.params.hide_display_duration_ms == null) {
                    return 24; // Default to 24 hours
                }
                return Math.round(this.params.hide_display_duration_ms / (60 * 60 * 1000));
            },
            set(value) {
                // Convert hours to milliseconds
                const hours = parseInt(value) || 24;
                this.params.hide_display_duration_ms = hours * 60 * 60 * 1000;
            }
        },

        integration_tags_with_none() {
            // Add "No Tag" option at the beginning of the list
            const no_tag_option = {
                tag_id: null,
                tag_label: 'No Tag'
            };
            return [no_tag_option, ...this.integration_tags];
        },

        showTagSelection() {
            if (!this.tag_new_lightbox_customers_available) {
                return false;
            }

            // Only show tag selection if either CRM upsert mode is set to create customers
            const creates_customers_on_submit = this.params &&
                (this.params.on_submit_crm_customer_upsert_mode === 'create-only' ||
                    this.params.on_submit_crm_customer_upsert_mode === 'create-or-update');

            const creates_customers_after_opt_in = this.params &&
                (this.params.after_double_opt_in_crm_customer_upsert_mode === 'create-only' ||
                    this.params.after_double_opt_in_crm_customer_upsert_mode === 'create-or-update');

            return creates_customers_on_submit || creates_customers_after_opt_in;
        },

        createLabelTextComputeds(field_names) {
            const computeds = {};

            field_names.forEach(field_name => {
                computeds[`${field_name}_label_text`] = {
                    get() {
                        if (!this.params || !this.params[field_name]) {
                            return { text: '', style: '', default_text: '', default_style: '' };
                        }

                        return {
                            text: this.params[field_name].text || '',
                            style: this.params[field_name].label_style || '',
                            default_text: this.params[field_name].default_text || '',
                            default_style: this.params[field_name].default_label_style || ''
                        };
                    },
                    set(value) {
                        if (this.params && this.params[field_name]) {
                            this.params[field_name].text = value.text;
                            this.params[field_name].label_style = value.style;
                        }
                    }
                };
            });

            return computeds;
        },

    },

    watch: {
        preview_mode(new_mode, old_mode) {
            // Reset to form page whenever preview mode changes
            if (new_mode !== old_mode) {
                this.current_page = 'form';
                this.lightbox_visible = true;
                this.onClickShowLightbox();
            }
        }
    },

}
</script>

<style lang="scss" scoped>
.outer_container {
    display: flex;

    position: absolute;
    top: 0;
    bottom: 0;
    left: 15px;
    right: 15px;
    gap: 10px;
}

.left_pane {
    flex-grow: 1;
    overflow-x: hidden;
    overflow-y: auto;
    padding-right: 15px;
    padding-bottom: 15px;
    min-width: 50%;
    max-width: 600px;
}

.right_pane_container {
    flex-grow: 1;

    overflow-x: hidden;
    overflow-y: hidden;

    background-image: url('https://files.redchirp.com/644eb29a-b21a-49fc-892a-32dbaad6531a.jpg');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;

    display: flex;
    justify-content: center;
    align-items: center;
}

/* Mobile preview keeps same background but with black frame around it */
.right_pane_container.mobile-preview {
    background: #ffffff;
    overflow-x: auto;
    overflow-y: auto;
    align-items: flex-start;
    padding: 20px;
}

/* Background is now handled by ::before pseudo-element */

.iframe-wrapper {
    width: 100%;
    height: 100%;
    position: relative;
}

.iframe-wrapper.mobile-frame {
    width: 375px;
    height: 667px;
    border: 8px solid #1a1a1a;
    border-radius: 40px;
    box-shadow:
        0 0 0 2px #333,
        0 8px 32px rgba(0, 0, 0, 0.4),
        inset 0 0 0 1px #444;
    background: #000;
    padding: 20px 12px;
    position: relative;
    overflow: visible;
}

/* Phone frame details */
.iframe-wrapper.mobile-frame::after {
    content: '';
    position: absolute;
    top: 12px;
    left: 50%;
    transform: translateX(-50%);
    width: 120px;
    height: 6px;
    background: #333;
    border-radius: 3px;
    z-index: 3;
}

/* Inner screen area with tree background */
.iframe-wrapper.mobile-frame::before {
    content: '';
    position: absolute;
    top: 20px;
    left: 12px;
    right: 12px;
    bottom: 20px;
    background-image: url('https://files.redchirp.com/644eb29a-b21a-49fc-892a-32dbaad6531a.jpg');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 24px;
    z-index: 1;
}

.right_pane_iframe {
    width: 100%;
    height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    border: none;
    position: relative;
    z-index: 2;
    /* Ensure lightbox appears above background */
}

.mobile-frame .right_pane_iframe {
    position: relative;
    z-index: 2;
    /* Ensure iframe appears above background */
    overflow-y: auto;
    /* Allow scrolling in mobile frame */
}

.mobile-label {
    position: absolute;
    bottom: -30px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 4px 12px;
    border-radius: 12px;
    font-size: 12px;
    white-space: nowrap;
}




@media only screen {
    .iframe-wrapper.mobile-frame {
        width: 390px;
        height: 844px;
    }
}

.section-header {
    margin-top: 24px;
    margin-bottom: 6px;
    font-weight: bold;
    display: flex;
    align-items: center;
}

.control-restore-btn {
    opacity: 0.7;
    transition: opacity 0.2s ease;
    font-size: 0.75rem !important;
    text-transform: none !important;
    letter-spacing: normal !important;
}

.control-restore-btn:hover {
    opacity: 1;
}

.control-restore-btn {
    font-size: 0.75rem !important;
    text-transform: none !important;
    letter-spacing: normal !important;
    opacity: 0.8;
    transition: opacity 0.2s ease;
}

.control-restore-btn:hover {
    opacity: 1;
}




</style>